import React from 'react';
import { TrendingUpIcon, TrendingDownIcon, DollarSignIcon, PackageIcon, UsersIcon, PlusIcon, AreaChartIcon, Clock8Icon, CheckCircleIcon, XCircleIcon, ActivityIcon } from 'lucide-react';
import { Button } from './ui/Button';
import { LiveMarketTicker } from './LiveMarketTicker';
const MarketplaceTag = ({
  marketplace
}) => {
  const styles = {
    amazon: 'bg-orange-500 text-white',
    swappa: 'bg-green-500 text-white',
    ebay: 'bg-blue-500 text-white',
    facebook: 'bg-indigo-500 text-white'
  };
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[marketplace.toLowerCase()]}`} data-id="element-114">
      {marketplace}
    </span>;
};
const StatusBadge = ({
  status
}) => {
  const styles = {
    pending: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    completed: 'bg-green-50 text-green-700 border-green-200',
    processing: 'bg-blue-50 text-blue-700 border-blue-200',
    rejected: 'bg-red-50 text-red-700 border-red-200'
  };
  const icons = {
    pending: <Clock8Icon className="h-3 w-3 mr-1" data-id="element-115" />,
    completed: <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-116" />,
    processing: <ActivityIcon className="h-3 w-3 mr-1" data-id="element-117" />,
    rejected: <XCircleIcon className="h-3 w-3 mr-1" data-id="element-118" />
  };
  return <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${styles[status]}`} data-id="element-119">
      {icons[status]}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>;
};
const MetricCard = ({
  title,
  value,
  change,
  isPositive,
  icon
}) => <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-3 md:p-4 shadow-sm" data-id="element-120">
    <div className="flex items-center justify-between mb-2 md:mb-4" data-id="element-121">
      <span className="text-xs md:text-sm text-gray-500 dark:text-gray-400" data-id="element-122">
        {title}
      </span>
      <div className="p-1.5 md:p-2 rounded-md bg-blue-50 dark:bg-brand-green/10 text-blue-600 dark:text-white" data-id="element-123">
        {icon}
      </div>
    </div>
    <div className="flex items-end justify-between" data-id="element-124">
      <span className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white" data-id="element-125">
        {value}
      </span>
      <span className={`text-xs md:text-sm font-medium flex items-center ${isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`} data-id="element-126">
        {isPositive ? <TrendingUpIcon className="h-3 w-3 md:h-4 md:w-4 mr-1" data-id="element-127" /> : <TrendingDownIcon className="h-3 w-3 md:h-4 md:w-4 mr-1" data-id="element-128" />}
        {change}
      </span>
    </div>
  </div>;
const RevenueChart = ({
  data
}) => {
  const maxValue = Math.max(...data.map(item => item.value));
  return <div className="h-[200px] flex items-end gap-2" data-id="element-129">
      {data.map((item, index) => <div key={index} className="flex-1 flex flex-col items-center group" data-id="element-130">
          <div className="relative w-full flex flex-col justify-end h-[160px]" data-id="element-131">
            <div className="w-full bg-blue-500 dark:bg-brand-green rounded-t transition-all duration-300 hover:opacity-80" style={{
          height: `${item.value / maxValue * 160}px`
        }} data-id="element-132" />
            <div className="absolute -top-8 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity z-10" data-id="element-133">
              <div className="bg-gray-900 text-white px-2 py-1 rounded text-xs whitespace-nowrap" data-id="element-134">
                ${item.value.toLocaleString()}
              </div>
            </div>
          </div>
          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400" data-id="element-135">
            {item.month}
          </div>
        </div>)}
    </div>;
};
const InventoryChart = ({
  data
}) => {
  const maxCount = Math.max(...data.map(item => item.count));
  return <div className="h-[200px] flex items-end gap-2" data-id="element-136">
      {data.map((item, index) => <div key={index} className="flex-1 flex flex-col items-center group" data-id="element-137">
          <div className="relative w-full flex flex-col justify-end h-[160px]" data-id="element-138">
            <div className="w-full bg-blue-500 dark:bg-brand-green rounded-t transition-all duration-300 hover:opacity-80" style={{
          height: `${item.count / maxCount * 160}px`
        }} data-id="element-139" />
            <div className="absolute -top-8 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity z-10" data-id="element-140">
              <div className="bg-gray-900 text-white px-2 py-1 rounded text-xs whitespace-nowrap" data-id="element-141">
                {item.count} units
              </div>
            </div>
          </div>
          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap" data-id="element-142">
            {item.model}
          </div>
        </div>)}
    </div>;
};
export function DashboardPreview() {
  const recentTrades = [{
    device: 'iPhone 13 Pro',
    customer: 'Alex Johnson',
    value: '$520',
    status: 'pending',
    time: '2 hours ago'
  }, {
    device: 'Samsung S21',
    customer: 'Maria Garcia',
    value: '$410',
    status: 'completed',
    time: '3 hours ago'
  }, {
    device: 'Google Pixel 6',
    customer: 'Robert Chen',
    value: '$380',
    status: 'processing',
    time: '5 hours ago'
  }, {
    device: 'iPhone 12 Pro',
    customer: 'Sarah Williams',
    value: '$390',
    status: 'rejected',
    time: '6 hours ago'
  }];
  const revenueTrends = [{
    month: 'Jan',
    value: 30000
  }, {
    month: 'Feb',
    value: 40000
  }, {
    month: 'Mar',
    value: 35000
  }, {
    month: 'Apr',
    value: 60000
  }, {
    month: 'May',
    value: 45000
  }, {
    month: 'Jun',
    value: 65000
  }, {
    month: 'Jul',
    value: 75000
  }];
  const inventoryData = [{
    model: 'iPhone 13',
    count: 45
  }, {
    model: 'iPhone 12',
    count: 32
  }, {
    model: 'Galaxy S22',
    count: 28
  }, {
    model: 'Pixel 6',
    count: 18
  }, {
    model: 'iPhone 11',
    count: 24
  }, {
    model: 'OnePlus 10',
    count: 15
  }];
  return <div className="w-full bg-gray-50 dark:bg-black p-4 md:p-6 space-y-4 md:space-y-6 overflow-auto max-h-[85vh]" data-id="element-143">
      <div className="mb-4 md:mb-6 bg-blue-50 dark:bg-brand-green/5 border border-blue-100 dark:border-brand-green/20 rounded-lg p-3 md:p-4" data-id="element-144">
        <div className="flex items-center justify-between" data-id="element-145">
          <div className="flex items-center gap-2 md:gap-3" data-id="element-146">
            <div className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-blue-100 dark:bg-brand-green/10 text-blue-500 dark:text-brand-green flex items-center justify-center" data-id="element-147">
              <ActivityIcon className="h-4 w-4 md:h-5 md:w-5" data-id="element-148" />
            </div>
            <div data-id="element-149">
              <h3 className="font-medium text-sm md:text-base text-gray-900 dark:text-white" data-id="element-150">
                Daily Market Insight
              </h3>
              <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400" data-id="element-151">
                iPhone 13 prices have stabilized after 3 weeks of decline. Good
                time to restock inventory.
              </p>
            </div>
          </div>
          <button className="text-xs md:text-sm px-2 py-1 md:px-3 md:py-1 rounded-md border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800" data-id="element-152">
            View Details
          </button>
        </div>
      </div>
      <div className="flex items-center justify-between" data-id="element-153">
        <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white" data-id="element-154">
          Dashboard
        </h1>
        <Button className="text-sm md:font-medium py-1 px-3 md:py-2 md:px-4 bg-blue-600 dark:bg-brand-green hover:bg-blue-700 dark:hover:bg-brand-green/90 text-white dark:text-black" data-id="element-155">
          <PlusIcon className="mr-1 md:mr-2 h-3 w-3 md:h-4 md:w-4" data-id="element-156" />
          New Trade-In
        </Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-id="element-157">
        <MetricCard title="Market Value" value="$45,231" change="+20.1%" isPositive={true} icon={<DollarSignIcon className="h-4 w-4" data-id="element-159" />} data-id="element-158" />
        <MetricCard title="Total Inventory" value="123" change="-2.3%" isPositive={false} icon={<PackageIcon className="h-4 w-4" data-id="element-161" />} data-id="element-160" />
        <MetricCard title="Revenue" value="$12,234" change="+12.4%" isPositive={true} icon={<DollarSignIcon className="h-4 w-4" data-id="element-163" />} data-id="element-162" />
        <MetricCard title="Active Traders" value="1,234" change="+4.3%" isPositive={true} icon={<UsersIcon className="h-4 w-4" data-id="element-165" />} data-id="element-164" />
      </div>
      <LiveMarketTicker data-id="element-166" />
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-sm" data-id="element-167">
        <div className="p-4 border-b border-gray-200 dark:border-gray-800" data-id="element-168">
          <div className="flex justify-between items-center" data-id="element-169">
            <div data-id="element-170">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-171">
                Recent Trade-In Requests
              </h2>
              <p className="text-sm text-gray-500" data-id="element-172">
                Latest device trade-in submissions
              </p>
            </div>
          </div>
        </div>
        <div className="p-4" data-id="element-173">
          <div className="overflow-x-auto" data-id="element-174">
            <table className="w-full" data-id="element-175">
              <thead data-id="element-176">
                <tr className="text-left text-sm text-gray-500" data-id="element-177">
                  <th className="pb-3 font-medium" data-id="element-178">Device</th>
                  <th className="pb-3 font-medium" data-id="element-179">Customer</th>
                  <th className="pb-3 font-medium" data-id="element-180">Value</th>
                  <th className="pb-3 font-medium" data-id="element-181">Status</th>
                  <th className="pb-3 font-medium" data-id="element-182">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800" data-id="element-183">
                {recentTrades.map((trade, index) => <tr key={index} className="text-sm" data-id="element-184">
                    <td className="py-3 text-gray-900 dark:text-white" data-id="element-185">
                      {trade.device}
                    </td>
                    <td className="py-3 text-gray-500" data-id="element-186">{trade.customer}</td>
                    <td className="py-3 font-medium text-gray-900 dark:text-white" data-id="element-187">
                      {trade.value}
                    </td>
                    <td className="py-3" data-id="element-188">
                      <StatusBadge status={trade.status} data-id="element-189" />
                    </td>
                    <td className="py-3 text-gray-500" data-id="element-190">{trade.time}</td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-191">
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-sm" data-id="element-192">
          <div className="p-4 border-b border-gray-200 dark:border-gray-800" data-id="element-193">
            <div className="flex justify-between items-center" data-id="element-194">
              <div data-id="element-195">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-196">
                  Revenue Trends
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-197">
                  Monthly revenue data
                </p>
              </div>
              <AreaChartIcon className="h-5 w-5 text-gray-400" data-id="element-198" />
            </div>
          </div>
          <div className="p-4" data-id="element-199">
            <RevenueChart data={revenueTrends} data-id="element-200" />
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-sm" data-id="element-201">
          <div className="p-4 border-b border-gray-200 dark:border-gray-800" data-id="element-202">
            <div className="flex justify-between items-center" data-id="element-203">
              <div data-id="element-204">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-205">
                  Inventory Breakdown
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-206">
                  Phones in stock by model
                </p>
              </div>
              <PackageIcon className="h-5 w-5 text-gray-400" data-id="element-207" />
            </div>
          </div>
          <div className="p-4" data-id="element-208">
            <InventoryChart data={inventoryData} data-id="element-209" />
          </div>
        </div>
      </div>
    </div>;
}